import {
  type BundleScope,
  type BundleTarget,
  type BundleWorkAssignment,
  type CatalogSnapshot,
  type ChangeRequestCategoryTemplate,
  type ChangeRequestReceipt,
  type ChangeRequestSet,
  type ChangeRequestSetCreate,
  type DraftAttachmentRow,
  type DraftSidecar,
  type EnqueueChangeRequestSetInput,
  type EnqueueSetAttachmentInput,
  type EnqueueSubmissionInput,
  type EnqueueSupplementInput,
  type FieldworkBundle,
  type IntakeCandidate,
  type IntakeCandidatesAnswer,
  type OutboxAssignmentRow,
  type OutboxAttachmentRow,
  type OutboxChangeRequestSetRow,
  type OutboxSubmissionRow,
  type OutboxSupplementRow,
  type OutboxTransitionRow,
  type StoredAssignment,
  type StoredCatalogInfo,
  type StoredDraft,
  type StoredDraftSidecar,
  type StoredFacility,
  type StoredKit,
  type StoredSupplement,
  type StoredTemplateVersion,
  type StoredUtilityRequirements,
  type StoredWalkupTemplate,
  type SubmissionRequestMembers,
  type SyncStack,
  type WorkAssignmentCreateBody,
  type WorkAssignmentStatus,
} from "./types";
import { type BlockedRequirement, type PostOutcome } from "./outbox-state";

// The storage surface the sync engine and the capture path require, split into
// six groups so an adapter under construction is typed by what it HAS rather
// than declared complete and filled with members that do nothing. Every member
// except the evidence three is the exact signature of its device-store
// counterpart with the connection handle dropped; the evidence three are a
// deliberate collapse of the platform staging surfaces.

/**
 * The read-only bundle snapshot: wholesale-replaced on every pull, zero merge
 * logic anywhere. Field scope wipes everything (the field bundle is cross-org);
 * org scope replaces one org's rows and prunes version/target rows no longer
 * referenced by any remaining assignment. Only a field-scope replace writes the
 * category templates, the change-request sets and the catalog versions (they
 * ride the field bundle alone); an org-scope replace leaves them as they were.
 * `getSyncMeta` / `setSyncMeta` are the snapshot's own key-value sidecar (last
 * pull timestamp, per-scope facility truncation flag), not a general store.
 */
export interface BundleStore {
  replaceBundle(scope: BundleScope, bundle: FieldworkBundle): Promise<void>;
  listBundleAssignments(): Promise<StoredAssignment[]>;
  getBundleAssignment(id: string): Promise<StoredAssignment | null>;
  setLocalStatus(assignmentId: string, localStatus: WorkAssignmentStatus | null): Promise<void>;
  getTemplateVersion(id: string): Promise<StoredTemplateVersion | null>;
  getTarget(facilityId: string, deviceInstallationId?: string | null): Promise<BundleTarget | null>;
  listBundleKits(): Promise<StoredKit[]>;
  listSupplements(): Promise<StoredSupplement[]>;
  supplementForCompany(
    companyId: string,
    deviceCategory: string | null,
  ): Promise<StoredSupplement | null>;
  getBundleFacility(facilityId: string): Promise<StoredFacility | null>;
  searchBundleFacilities(q: string, limit?: number): Promise<StoredFacility[]>;
  listWalkupTemplates(orgId: string): Promise<StoredWalkupTemplate[]>;
  /**
   * The org's readiness block as the last pull left it, or null when the
   * bundle carried none for that org — which the offline start gate reads as
   * an unknown, and therefore open, gate.
   */
  getUtilityRequirements(orgId: string): Promise<StoredUtilityRequirements | null>;
  /** The wire template for an org's device category verbatim, or null. */
  getCategoryTemplate(
    orgId: string,
    deviceCategory: string,
  ): Promise<ChangeRequestCategoryTemplate | null>;
  /** The bundle's change-request sets verbatim, in the bundle's order. */
  listBundleChangeRequestSets(): Promise<ChangeRequestSet[]>;
  /** The bundle's raw catalog token per org id. */
  getCatalogVersions(): Promise<Record<string, string>>;
  insertLocalWalkupAssignment(input: {
    assignment: BundleWorkAssignment;
    facility: StoredFacility;
  }): Promise<void>;
  getSyncMeta(key: string): Promise<string | null>;
  setSyncMeta(key: string, value: string): Promise<void>;
}

/**
 * Draft answer documents plus staged-evidence bookkeeping for in-progress
 * captures. A locked draft is sealed — the enqueue transaction locks it — and
 * immutable until cleanup deletes it; `setDraftKit` no-ops against a locked row.
 * The attachment members record WHICH evidence belongs to a draft; the bytes
 * themselves are the evidence store's concern. Their `assignmentId` slot is the
 * evidence OWNER (see DraftAttachmentRow.assignmentId): a change request's
 * photos stage under its client set id, never under the test's assignment id.
 *
 * The sidecar members hold a draft's panel state (`DraftSidecar`) under an
 * assignment id or a client set id. A sidecar is unsynced work; an
 * acknowledged test's cleanup (`markCleanedUp`) deletes its assignment's
 * sidecar last, after the rows and bytes under every owner it names.
 */
export interface DraftStore {
  saveDraft(assignmentId: string, answers: Record<string, unknown>, now?: number): Promise<void>;
  getDraft(assignmentId: string): Promise<StoredDraft | null>;
  setDraftKit(assignmentId: string, testKitId: string | null): Promise<void>;
  lockDraft(assignmentId: string): Promise<void>;
  deleteDraft(assignmentId: string): Promise<void>;
  stageDraftAttachment(row: DraftAttachmentRow): Promise<void>;
  listDraftAttachments(assignmentId: string): Promise<DraftAttachmentRow[]>;
  removeDraftAttachment(clientAttachmentId: string): Promise<void>;
  /** Writes (or overwrites) the sidecar under `key`. */
  saveDraftSidecar(key: string, sidecar: DraftSidecar): Promise<void>;
  loadDraftSidecar(key: string): Promise<DraftSidecar | null>;
  /** Every stored sidecar, key-ascending. */
  listDraftSidecars(): Promise<StoredDraftSidecar[]>;
  deleteDraftSidecar(key: string): Promise<void>;
}

/**
 * The append-only regulatory outbox over the pure machine in `outbox-state.ts`,
 * across its five row families: submissions with their attachments, assignment
 * transitions, walk-up assignment creates, company supplements, and
 * change-request sets with theirs. Every row snapshots which stack (and which
 * org) it replays against — the current app mode is irrelevant at replay time;
 * a set replays on the field stack, its org inside its body. UUIDv7 primary
 * keys sort chronologically, which is what makes FIFO replay a plain ordered
 * read.
 *
 * `enqueueSubmission` is all-or-nothing: the outbox row, every attachment row,
 * and the draft lock land together or not at all, and the evidence the
 * attachment rows reference MUST already be durably staged when it commits.
 * `enqueueChangeRequestSet` holds the same contract for the set row and its
 * attachment rows.
 */
export interface OutboxStore {
  enqueueSubmission(input: EnqueueSubmissionInput, now?: number): Promise<void>;
  nextPending(): Promise<OutboxSubmissionRow | null>;
  markSending(clientSubmissionId: string, now?: number): Promise<void>;
  applyPostOutcome(
    clientSubmissionId: string,
    outcome: PostOutcome | null,
    now?: number,
  ): Promise<void>;
  listAttachmentsForSubmission(clientSubmissionId: string): Promise<OutboxAttachmentRow[]>;
  markAttachmentSending(clientAttachmentId: string): Promise<void>;
  markAttachmentUploaded(
    clientAttachmentId: string,
    submissionStatus: string,
    attachmentsComplete: boolean,
    now?: number,
  ): Promise<void>;
  /**
   * An acknowledged row's terminal hop to `synced`, and the end of what was
   * staged for its test: the assignment's sidecar goes with it, after every
   * draft-attachment row and staged byte under each owner the sidecar names
   * (`sidecarEvidenceOwners`). A row that is not acknowledged is a no-op.
   */
  markCleanedUp(clientSubmissionId: string, now?: number): Promise<void>;
  /**
   * The way back from `blocked`: the named row returns to `finalized` for the
   * next drain and loses its blocked requirements, from `blocked` ONLY — any
   * other state is a no-op. `attempts` and the attachment rows are untouched:
   * the document and its evidence are the same regulatory capture, refused
   * once and now cured, not a new submission.
   */
  retryBlocked(clientSubmissionId: string, now?: number): Promise<void>;
  /**
   * The cure of a `blocked` test whose change request lacked evidence: from
   * `blocked` ONLY, back to `finalized` without its blocked requirements, the
   * payload's `device_of_record` and `change_requests` replaced (under the
   * same client ids) and the new attachment rows added. The sealed `responses`
   * and every other payload member stay byte-identical.
   */
  amendBlockedSubmission(
    clientSubmissionId: string,
    requestMembers: SubmissionRequestMembers,
    addedAttachments: EnqueueSetAttachmentInput[],
    now?: number,
  ): Promise<void>;
  /**
   * Reverts an interrupted run's in-flight rows: `sending` returns to its
   * pre-flight state in every queue (a set to `queued` — its create is
   * idempotent), a `sending` attachment to `staged`. A set in
   * `uploading_evidence` stays there with its receipt, so its uploads resume.
   */
  recoverInFlight(now?: number): Promise<void>;
  pendingCount(): Promise<number>;
  listForAssignment(assignmentId: string): Promise<OutboxSubmissionRow[]>;
  getOutboxSubmission(clientSubmissionId: string): Promise<OutboxSubmissionRow | null>;
  listVisible(): Promise<OutboxSubmissionRow[]>;
  queueTransition(
    input: {
      assignmentId: string;
      orgId: string;
      stack: SyncStack;
      toStatus: WorkAssignmentStatus;
    },
    now?: number,
  ): Promise<void>;
  nextQueuedTransition(): Promise<OutboxTransitionRow | null>;
  markTransitionSynced(assignmentId: string): Promise<void>;
  enqueueWalkupAssignment(
    input: {
      assignmentId: string;
      orgId: string;
      payload: WorkAssignmentCreateBody;
    },
    now?: number,
  ): Promise<void>;
  listPendingWalkupAssignments(): Promise<OutboxAssignmentRow[]>;
  nextQueuedAssignment(): Promise<OutboxAssignmentRow | null>;
  markAssignmentSending(assignmentId: string, now?: number): Promise<void>;
  markAssignmentSynced(assignmentId: string, now?: number): Promise<void>;
  markAssignmentError(assignmentId: string, lastError: string, now?: number): Promise<void>;
  markAssignmentRetry(assignmentId: string, now?: number): Promise<void>;
  enqueueSupplement(input: EnqueueSupplementInput, now?: number): Promise<void>;
  nextPendingSupplement(): Promise<OutboxSupplementRow | null>;
  listPendingSupplements(): Promise<OutboxSupplementRow[]>;
  markSupplementSending(clientSubmissionId: string, now?: number): Promise<void>;
  markSupplementSynced(clientSubmissionId: string, now?: number): Promise<void>;
  markSupplementError(clientSubmissionId: string, lastError: string, now?: number): Promise<void>;
  markSupplementRetry(clientSubmissionId: string, lastError: string, now?: number): Promise<void>;
  getOutboxSupplement(clientSubmissionId: string): Promise<OutboxSupplementRow | null>;
  /** The set row lands `queued` with every attachment row, or nothing lands. */
  enqueueChangeRequestSet(input: EnqueueChangeRequestSetInput, now?: number): Promise<void>;
  /** Every set row in FIFO (key) order, whatever its state. */
  listChangeRequestSetRows(): Promise<OutboxChangeRequestSetRow[]>;
  listAttachmentsForChangeRequestSet(clientSetId: string): Promise<OutboxAttachmentRow[]>;
  /**
   * From `needs_answer` ONLY, back to `queued` with `candidates` cleared. A
   * pick makes the body `standalone` on that facility, its `intake` kept; none
   * of them writes every held candidate into `dismissed_candidates` and stamps
   * `candidates_checked_at` (`now`), so the set is never checked again.
   */
  answerIntakeCandidates(
    clientSetId: string,
    answer: IntakeCandidatesAnswer,
    now?: number,
  ): Promise<void>;
  /** From `blocked` ONLY: back to `queued` without its blocked requirements. */
  retryBlockedChangeRequestSet(clientSetId: string, now?: number): Promise<void>;
  /**
   * From `blocked` ONLY: the body replaced (the caller keeps its client set
   * and request ids), the new attachment rows added with their evidence
   * already staged, back to `queued` without its blocked requirements.
   */
  amendBlockedChangeRequestSet(
    clientSetId: string,
    body: ChangeRequestSetCreate,
    addedAttachments: EnqueueSetAttachmentInput[],
    now?: number,
  ): Promise<void>;
  /**
   * From `error` with no receipt ONLY — a set the utility never received: the
   * set is a draft again. Each of its attachment rows returns to a draft
   * attachment row under the owner it was staged under (a staged test's file
   * under its link, every other file under the set), and the set row and its
   * attachment rows go, all in one transaction; the staged bytes stay. The
   * next enqueue under the same client set id is then the set's one row. A
   * returned row carries the `fileName` and `stagedAt` its outbox row kept.
   */
  reopenChangeRequestSet(clientSetId: string): Promise<void>;
  // The sets phase's transitions. The candidate check's three outcomes act on
  // a `queued` row only; every stamp that settles a check clears
  // `lastCheckFailedAt`.
  markChangeRequestSetNeedsAnswer(
    clientSetId: string,
    candidates: IntakeCandidate[],
    now?: number,
  ): Promise<void>;
  /** No candidate: `candidates_checked_at` (`now`) is stamped into the body; the row stays `queued`. */
  markChangeRequestSetChecked(clientSetId: string, now?: number): Promise<void>;
  /** A retryable check failure: the row stays `queued` with `lastCheckFailedAt` = `now`. */
  markChangeRequestSetCheckFailed(clientSetId: string, now?: number): Promise<void>;
  markChangeRequestSetSending(clientSetId: string, now?: number): Promise<void>;
  /**
   * From `sending`: the receipt is stored and the row moves to
   * `uploading_evidence` — or straight to `received` when the receipt's set no
   * longer awaits evidence.
   */
  storeChangeRequestSetReceipt(
    clientSetId: string,
    receipt: ChangeRequestReceipt,
    now?: number,
  ): Promise<void>;
  /** Marks one set upload acked; a complete set moves `uploading_evidence` → `received`. */
  markChangeRequestSetAttachmentUploaded(
    clientAttachmentId: string,
    setAttachmentsComplete: boolean,
    now?: number,
  ): Promise<void>;
  markChangeRequestSetBlocked(
    clientSetId: string,
    blockedRequirements: BlockedRequirement[],
    now?: number,
  ): Promise<void>;
  markChangeRequestSetError(clientSetId: string, lastError: string, now?: number): Promise<void>;
  /** A retryable create failure: back to `queued`, attempts++, the cause recorded. */
  markChangeRequestSetRetry(clientSetId: string, lastError: string, now?: number): Promise<void>;
  /**
   * A received set's terminal hop, and the end of what was staged for it: the
   * set's sidecar goes with it, after every draft-attachment row and staged
   * byte under each owner the sidecar names (`sidecarEvidenceOwners`). A set
   * that is not `received` is a no-op.
   */
  markChangeRequestSetSynced(clientSetId: string, now?: number): Promise<void>;
}

/**
 * The bytes to stage, in a form BOTH adapters can consume. Mobile's three
 * staging entry points collapse onto this one member: `stagePhoto` and
 * `stageFile` hand a captured/picked source address, `stageSignaturePng` hands
 * a data URL — so the body is a union, not a `Blob`. A filesystem adapter
 * copies a `{ uri }` body and decodes a `{ dataUrl }` one; an IndexedDB adapter
 * puts a `{ blob }` body and converts the other two. Each adapter implements
 * every arm it can serve and REJECTS an arm it cannot, naming the arm.
 */
export type EvidenceBody =
  | { kind: "uri"; uri: string }
  | { kind: "dataUrl"; dataUrl: string }
  | { kind: "blob"; blob: Blob };

export interface StageEvidenceInput {
  /** The evidence owner — see DraftAttachmentRow.assignmentId. */
  assignmentId: string;
  /**
   * Minted by the CALLER, never by the adapter. It is the wire's address for
   * this attachment, so it must exist before staging can succeed OR fail — a
   * failed stage still has to be reportable against an id.
   */
  clientAttachmentId: string;
  contentType: string;
  body: EvidenceBody;
}

/**
 * One multipart part, in whichever form the platform's FormData accepts: a
 * `Blob` in a browser, a descriptor in React Native. The union is what lets the
 * single transport line `formData.append("file", part, filename)` serve both —
 * React Native's FormData reads the filename off the descriptor and ignores the
 * third argument, the browser's reads it from the third argument and the `Blob`
 * carries none. Narrowing this to `Blob` would lock mobile out of the contract.
 */
export type EvidencePart = Blob | { uri: string; name: string; type: string };

/**
 * The multipart filename's extension, contract-pinned rather than
 * adapter-chosen. Both adapters derive the same string for the same content
 * type; without one map the extension is a coin flip (`jpg` vs `jpeg`) and the
 * two platforms' multipart headers diverge with nothing catching it, because
 * the locator's own form is deliberately opaque and never asserted.
 */
export const EVIDENCE_FILENAME_EXTENSIONS: Readonly<Record<string, string>> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "application/pdf": "pdf",
};

/** The extension for a content type outside the pinned map. */
export const EVIDENCE_FALLBACK_EXTENSION = "bin";

/** The `<clientAttachmentId>.<ext>` filename `readEvidencePart` must return. */
export function evidenceFilename(clientAttachmentId: string, contentType: string): string {
  const ext = EVIDENCE_FILENAME_EXTENSIONS[contentType] ?? EVIDENCE_FALLBACK_EXTENSION;
  return `${clientAttachmentId}.${ext}`;
}

/**
 * Evidence is its own storage domain, and the asymmetry between the two
 * adapters is the reason it is stated rather than assumed. The all-or-nothing
 * enqueue covers the outbox row, the attachment rows and the draft lock, and it
 * REQUIRES the referenced evidence to be durably staged BEFORE it commits — on
 * a filesystem adapter that staging happens outside the transaction domain
 * entirely, on IndexedDB it happens inside the same database. What the contract
 * demands is reference integrity at commit, not transactional co-location.
 *
 * `readEvidencePart` REJECTS when the staged bytes are absent: a queued
 * regulatory submission whose evidence has vanished is a loud failure, never a
 * silently empty upload.
 */
export interface EvidenceStore {
  stageEvidence(
    input: StageEvidenceInput,
  ): Promise<{ clientAttachmentId: string; locator: string; contentType: string }>;
  /**
   * Reads the staged bytes for an attachment row and the wire filename the
   * upload uses. Typed on the structural subset both row shapes share so a
   * DRAFT attachment (which has no submission id, state or attempt count) can
   * be read back for a restored capture without a cast; `OutboxAttachmentRow`
   * satisfies it unchanged.
   */
  readEvidencePart(row: {
    clientAttachmentId: string;
    locator: string;
    contentType: string;
  }): Promise<{ part: EvidencePart; filename: string }>;
  deleteEvidenceForAssignment(assignmentId: string): Promise<void>;
}

/**
 * Profile lifecycle. The snapshot drop keeps unsynced residue — queued outbox
 * rows, their staged evidence, unlocked drafts, draft sidecars — because that
 * residue is regulatory work the server has not accepted yet; the purge is the
 * deliberate end of a profile's local existence and keeps nothing.
 * `hasUnsyncedWork` is the drained-ness predicate the two are chosen between.
 * Stored catalogs are reference data, never residue: the snapshot drop removes
 * them with the bundle.
 */
export interface LifecycleStore {
  /**
   * Whether ANY unsynced family is non-empty. The exact predicate: a row with
   * `state !== "synced"` in any of the five outbox queues — submissions,
   * walk-up assignment creates, transitions, supplements, change-request sets
   * — or ANY draft row, ANY draft sidecar, or ANY staged draft-attachment row. Error, revoked and blocked rows count
   * as unsynced: they are retained regulatory state, not drained work — and a
   * blocked row is work that resumes the moment its requirement is cured.
   * This is the logout-time gate the purge/residue split branches on, and it deliberately
   * covers every family the residue path pins — unlike `pendingCount`, the
   * sync-bar counter, which reads only submissions + walk-up creates and must
   * never gate a purge.
   *
   * Staged evidence counts through its own attachment ROW rather than through
   * a draft, because attachment rows do not reference drafts and the capture
   * path writes them first: the port stages the bytes and inserts the
   * attachment row, then routes the answer change to a debounced draft save.
   * An attachment row with no draft row is therefore a real, durable state,
   * and a drafts-only reading would call it drained and purge a compliance
   * photo out of existence.
   *
   * The staged BYTES are deliberately not a family of their own. One adapter
   * keeps them as rows and the other as a filesystem tree, so the row is the
   * only unit both can count identically; and rows are dropped ahead of their
   * bytes (attachment removal and draft deletion leave cleanup to the evidence
   * store), so counting orphaned bytes would hold this predicate true forever
   * and permanently disable the purge that reclaims them. Bytes missing under
   * a live row are already a loud failure — `readEvidencePart` rejects.
   */
  hasUnsyncedWork(): Promise<boolean>;
  dropBundleSnapshotPreservingUnsyncedResidue(): Promise<void>;
  purgeProfileDatabase(): Promise<void>;
}

/**
 * The per-org catalog document (`CatalogSnapshot`), stored wholesale and
 * verbatim with its RAW token as `etag` — the bundle's `catalog_versions`
 * value, never the quoted header — so the engine compares raw tokens only.
 * A second save for an org replaces the first. Reference data, not unsynced
 * work: the snapshot drop and the purge both remove it.
 */
export interface CatalogStore {
  save(orgId: string, etag: string, document: CatalogSnapshot, now?: number): Promise<void>;
  load(orgId: string): Promise<CatalogSnapshot | null>;
  /** Every stored catalog's metadata, org-id-ascending. */
  list(): Promise<StoredCatalogInfo[]>;
  remove(orgId: string): Promise<void>;
}

export type StorageAdapter = BundleStore &
  DraftStore &
  OutboxStore &
  EvidenceStore &
  LifecycleStore &
  CatalogStore;
